import { useForm, zodResolver } from '@mantine/form'
import { AxiosError } from 'axios'
import { useEffect } from 'react'
import { z } from 'zod'
import { create } from 'zustand'

import { useMutationUpdateUserLogged } from '@flecto/core/shared'
import { useTranslation } from '@flecto/translations'
import { type CoreUser, type CoreUserPayload } from '@flecto/types'
import { useNotifications } from '@flecto/ui'
import { getImageUrl } from '@flecto/utils'

export type UseFormProfileAccountOptions = {
  user?: Partial<CoreUserPayload & CoreUser>
}

export type FormProfileAccountState = {
  isLoading: boolean
  setIsLoading: (isLoading: boolean) => void
}

export type FormProfileAccountData = Partial<
  CoreUserPayload & CoreUser & { avatar_image_url: string }
>

const formState = create<FormProfileAccountState>((set) => ({
  isLoading: false,
  setIsLoading: (isLoading: boolean) => set({ isLoading }),
}))

export function useFormProfileAccount(options?: UseFormProfileAccountOptions) {
  const { t: tv } = useTranslation('shared', { keyPrefix: 'validation' })
  const { t } = useTranslation('saas', { keyPrefix: 'profile_account' })

  const { setIsLoading, isLoading} = formState()

  const { showNotification } = useNotifications()

  const { mutateAsync: updateUser } = useMutationUpdateUserLogged()

  const user = options?.user ?? {}

  const validateStringPlain = z.string({
    required_error: tv('required'),
    invalid_type_error: tv('required'),
  })

  const validateString = validateStringPlain
    .min(2, { message: tv('required') })
    .trim()
    .refine((val) => val.trim().length > 0, { message: tv('required') })

  const schema = z.object({
    full_name: validateString,
    phone_number: validateString,
  })

  const image = user?.links?.avatar_url ? getImageUrl(user?.links.avatar_url) : undefined

  const initialValues: FormProfileAccountData = {
    full_name: '',
    phone_number: '',
    avatar_image_id: undefined,
    avatar_image_url: image,
    meta: {
      phone_number_country_code: user.meta?.phone_number_country_code ?? '',
      ...user.meta,
    } as CoreUser['meta'],
    ...user,
  }

  const form = useForm({
    initialValues,
    validate: zodResolver(schema),
    validateInputOnBlur: true,
    validateInputOnChange: true,
  })

  const submit = async (values: FormProfileAccountData) => {
    try {
      setIsLoading(true)
      const payload: FormProfileAccountData = {
        full_name: values.full_name,
        phone_number: `${values.meta?.phone_number_country_code}${values.phone_number}`,
      }

      if (values.avatar_image_id !== undefined) {
        payload.avatar_image_id = values.avatar_image_id
      }

      await updateUser({
        user: payload,
      })

      showNotification({
        type: 'success',
        message: t('notifications.alert_success_user_updated'),
      })
    } catch (error) {
      if (error instanceof AxiosError) {
        const message =
          error?.response?.data?.errors[0]?.detail ||
          t('notifications.alert_error_general')

        showNotification({ message, type: 'error' })
      }
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (form.errors) {
      console.error(form.errors)
    }
  }, [form.errors])

  return { form, submit, isLoading }
}
